import { RequestStatus } from "hooks/useRequest";
import { MultiSelect } from "react-multi-select-component";
import {
  CSSObjectWithLabel,
  OptionProps,
  Theme,
  ThemeConfig,
} from "react-select";
import styled from "styled-components";
import colours from "styles/colours";
import fonts from "styles/fonts";
import shadows from "styles/shadows";
import { customSelectTheme } from "styles/themes";

import { FilterOption } from "./helpers";

const addErrorColours: ThemeConfig = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    neutral20: colours.elexonRed, // Border colour
  },
});

export const augmentTheme = (
  baseTheme: Theme,
  requestStatus: RequestStatus
): Theme => {
  const theme = customSelectTheme(baseTheme);
  switch (requestStatus) {
    case RequestStatus.ERRORED:
      return addErrorColours(theme);
    default:
      return {
        ...theme,
        colors: {
          ...theme.colors,
          primary: colours.elexonBlue, // Border colour when active
          neutral20: colours.elexonBlue, // Border colour
          neutral30: colours.elexonBlue, // Border colour on hover
        },
      };
  }
};

export const StyledMultiSelect = styled(MultiSelect)`
  --rmsc-hover: ${colours.lightHighlightGrey};
  --rmsc-selected: ${colours.lightHighlightGrey};

  > div > * {
    height: 100%;
    font-size: 1rem;
    font-weight: 400;
  }

  > div:nth-child(1) {
    height: 3rem;
    border-radius: 32px;
    display: flex;
    align-items: center;
    border: 1px solid ${colours.elexonBlue};

    &:focus,
    &:active,
    &:focus-within {
      border-color: ${colours.elexonBlue};
      box-shadow: none;
      outline: none;
    }

    &:focus-visible {
      outline: 1px solid ${colours.elexonBlack};
      box-shadow: none;
    }

    > div:nth-child(2) {
      background: transparent;
      box-shadow: none;

      > div:nth-child(1) {
        margin-top: -6px;
        border-radius: 8px;
        box-shadow: ${shadows.shadowMd};
        border: 1px solid ${colours.lightGrey};
      }
    }
  }

  ul.options > label.select-item:first-child,
  ul.options > label.select-item:first-child:hover,
  ul.options > label.select-item:first-child.selected {
    background-color: ${colours.white};
  }
`;

export const StyledLabel = styled.div`
  font-size: 1rem;
`;

export const SelectAllRow = styled.div`
  display: flex;
  gap: 0.63rem;
`;

export const ValueRendererLabel = styled.div`
  display: flex;
`;

export const Placeholder = styled.div`
  color: ${colours.mediumGrey};
`;

export const BoldWord = styled.div`
  font-weight: ${fonts.semibold};
`;

export const Divider = styled.div`
  width: 0.06rem;
  height: 1.25rem;
  background: ${colours.highlightGrey};
`;

export const dropdownStyles = {
  control: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    minHeight: "3rem",
    height: "auto",
    borderRadius: "200px",
    boxShadow: "none",
  }),
  menu: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    marginTop: 0,
    boxShadow: shadows.shadowMd,
    border: `1px solid ${colours.lightGrey}`,
    borderRadius: 8,
    overflow: "hidden",
  }),
  menuList: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  option: (
    provided: CSSObjectWithLabel,
    state: OptionProps<FilterOption, false>
  ): CSSObjectWithLabel => ({
    ...provided,
    backgroundColor: state.isFocused
      ? colours.lightHighlightGrey
      : colours.white,
    color: colours.darkGrey,
    ":active": {
      backgroundColor: colours.lightHighlightGrey,
    },
  }),
  valueContainer: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    paddingLeft: "1rem",
    paddingRight: "0.25rem",
  }),
  indicatorsContainer: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    paddingRight: "0.75rem",
  }),
  placeholder: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    color: colours.mediumGrey,
  }),
};

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 15.6rem;
`;

export const MiniInputContainer = styled(InputContainer)`
  width: 7rem;
`;

export const AutoInputContainer = styled(InputContainer)`
  width: auto;
`;

export const BoldedLabel = styled.label`
  font-weight: ${fonts.semibold};
  color: ${colours.elexonBlack};
`;
